import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import {
  Alert,
  Box,
  Button,
  Heading,
  HelperText,
  Label,
  Stack,
  TextInput,
  TextInputControl,
} from "@deliveryhero/cape-core";
import { ArrowBackwardIcon } from "@deliveryhero/cape-icons";
import { useEffect, useState } from "react";
import { Form, Link, useActionData, useNavigation } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { env } from "~/config";
import { i18n } from "~/lib/i18n";
import { getBrandName } from "~/lib/meta";
import type { Route } from "./+types/route";
import ForgotPasswordBrandedSvg from "./forgot-password-branded.svg?react";
import ForgotPasswordNeutralSvg from "./forgot-password-neutral.svg?react";
import * as styles from "../verify-email/verify-email.css";

const BLOCKED_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com"];

const ForgotPasswordSchema = z.object({
  email: z
    .string({ required_error: "forgot_password.email.error.required" })
    .min(1, "forgot_password.email.error.required")
    .email("forgot_password.email.error.invalid")
    .refine((v) => !BLOCKED_DOMAINS.includes(v.split("@")[1]?.toLowerCase() ?? ""), {
      message: "forgot_password.email.error.nonWork",
    }),
});

export function meta({ matches }: Route.MetaArgs) {
  const brandName = getBrandName(matches);
  return [
    { title: i18n.t("forgot_password.meta.title", { brandName }) },
    { name: "description", content: i18n.t("forgot_password.meta.description", { brandName }) },
  ];
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const submission = parseWithZod(formData, { schema: ForgotPasswordSchema });

  if (submission.status !== "success") {
    return { lastResult: submission.reply(), sent: false };
  }

  const { email } = submission.value;

  if (!env.apiBaseUrl) {
    return { sent: true, lastResult: null };
  }

  try {
    await fetch(`${env.apiBaseUrl}/api/v1/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch {
    // always confirm — no enumeration
  }

  return { sent: true, lastResult: null };
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const sent = actionData?.sent === true;

  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!sent) return;
    setCountdown(60);
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [sent]);

  const [form, fields] = useForm({
    lastResult: actionData?.lastResult,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema });
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  if (sent) {
    return (
      <main className={styles.container}>
        <Box className={styles.card}>
          <Link to="/auth/login" className={styles.backLink} data-testid="back-link">
            <ArrowBackwardIcon
              aria-hidden="true"
              width="var(--cp-icon-sizing-small)"
              height="var(--cp-icon-sizing-small)"
            />
            {t("forgot_password.cta.backToLogin")}
          </Link>

          <Alert variant="info" style={{ marginBlockEnd: "var(--cp-spacing-large)" }}>
            {t("forgot_password.sent.message")}
          </Alert>

          {countdown > 0 ? (
            <Box className={styles.cooldownText}>
              {t("forgot_password.sent.cooldown", { seconds: countdown })}
            </Box>
          ) : (
            <Link to="/auth/forgot-password" className={styles.link}>
              <Button variant="tertiary" size="small" className={styles.fullWidth}>
                {t("forgot_password.sent.resend")}
              </Button>
            </Link>
          )}
        </Box>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <Box className={styles.card}>
        <Link to="/auth/login" className={styles.backLink} data-testid="back-link">
          <ArrowBackwardIcon
            aria-hidden="true"
            width="var(--cp-icon-sizing-small)"
            height="var(--cp-icon-sizing-small)"
          />
          {t("forgot_password.cta.backToLogin")}
        </Link>

        <Box className={styles.header}>
          <Box className={styles.fpIllustration} aria-hidden="true">
            <Box className={styles.fpIllustrationLayerNeutral}>
              <ForgotPasswordNeutralSvg className={styles.illustrationImg} />
            </Box>
            <Box className={styles.fpIllustrationLayerBranded}>
              <ForgotPasswordBrandedSvg className={styles.illustrationImg} />
            </Box>
          </Box>
          <Heading level={1} className={styles.title}>
            {t("forgot_password.heading")}
          </Heading>
          <Box as="p" className={styles.subtitle}>
            {t("forgot_password.subtitle")}
          </Box>
        </Box>

        <Form method="post" {...getFormProps(form)}>
          <Stack direction="column" spacing="--cp-spacing-3xlarge">
            <Box>
              <Label htmlFor={fields.email.id} required>
                {t("forgot_password.email.label")}
              </Label>
              <TextInput
                {...(fields.email.errors?.length ? { validationState: "error" as const } : {})}
              >
                <TextInputControl
                  {...getInputProps(fields.email, { type: "email" })}
                  placeholder={t("forgot_password.email.placeholder")}
                  autoComplete="email"
                  disabled={isSubmitting}
                />
              </TextInput>
              {fields.email.errors != null && fields.email.errors.length > 0 && (
                <HelperText id={fields.email.errorId} variant="error" role="alert">
                  {t(fields.email.errors[0] ?? "")}
                </HelperText>
              )}
            </Box>

            <Button
              type="submit"
              variant="primary"
              size="small"
              className={styles.fullWidth}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("forgot_password.submitting") : t("forgot_password.submit")}
            </Button>
          </Stack>
        </Form>
      </Box>
    </main>
  );
}

export function ErrorBoundary() {
  const { t } = useTranslation();
  return (
    <main className={styles.container}>
      <Box className={styles.card}>
        <Alert variant="error">{t("error.default.message")}</Alert>
      </Box>
    </main>
  );
}
